// IncomingRequestTrends.jsx
import React, { useMemo } from "react";
import Chart from "react-apexcharts";

import { Box, CircularProgress, Stack } from "@mui/material";
import useHome from "../../../../../hooks/user/useHome";
import colors from "../../../../../utils/colors";
import { withApexChartFont, apexVerticalAreaGradient, apexNoOfYAxisTitle } from "../../../../../utils/apexChartDefaults";

const IncomingRequestTrends = () => {
  const { traffics } = useHome();

  const chartData = useMemo(() => {
    return Array.isArray(traffics)
      ? traffics.map((item) => {
          return [new Date(item?.key_as_string).getTime(), item?.nr_entries];
        })
      : [];
  }, [traffics]);

  const series = useMemo(() => {
    return [
      {
        name: "Incoming Request Trends",
        data: chartData,
      },
    ];
  }, [chartData]);

  const hasFewPoints = chartData.length <= 3;

  const options = withApexChartFont({
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
      sparkline: { enabled: false },
      foreColor: "#696969",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["rgba(29, 215, 91, 1)"],
    stroke: {
      curve: "smooth",
      width: hasFewPoints ? 2 : 0,
    },
    fill: apexVerticalAreaGradient({
      toColor: "rgba(29, 215, 91, 0.25)",
    }),
    markers: {
      size: hasFewPoints ? 4 : 0,
      colors: ["rgba(29, 215, 91, 1)"],
      strokeColors: "#fff",
      strokeWidth: 2,
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        style: { fontSize: "12px", colors: "#696969" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: 2, // shows left + middle + right ticks in many datasets
    },
    yaxis: {
      tickAmount: 4, // 3 ticks: 0,50,100
      title: apexNoOfYAxisTitle("No. of Requests"),
      labels: {
        style: { colors: "#696969", fontSize: "12px" },
        formatter: (val) => Math.round(val),
      },
    },
    grid: {
      show: true,
      borderColor: "#e6e6e6",
      strokeDashArray: 0,
      padding: { left: 8 },
      xaxis: { lines: { show: false } },
      yaxis: {
        lines: { show: true }, // show horizontal grid lines
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: true,
      x: { show: false }, // hide the x inside tooltip (we only want value)
      // custom receives context with series, seriesIndex, dataPointIndex, w
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex];
        // you can compute arrow up/down etc. here. sample: compare prev value.
        let arrow = "";
        let arrowClass = "";
        if (dataPointIndex > 0) {
          const prev = series[seriesIndex][dataPointIndex - 1];
          if (value >= prev) {
            arrow = "▲";
            arrowClass = "up";
          } else {
            arrow = "▼";
            arrowClass = "down";
          }
        }
        // return HTML string for tooltip content
        return `
          <div class="custom-tooltip">
            <div class="value-row">
              <span class="value-text ${arrowClass}">${value} ${arrow}</span>
            </div>
          </div>
        `;
      },
    },
    legend: { show: false },
  });

  return traffics === null || traffics === undefined ? (
    <Stack alignItems={"center"} justifyContent={"center"} sx={{ height: "100%" }}>
      <CircularProgress sx={{ color: colors.neutral[400] }} />
    </Stack>
  ) : (
    <Box sx={{ width: "100%", pr: 3 }}>
      <Chart options={options} series={series} type="area" height={170} />
    </Box>
  );
};
export default IncomingRequestTrends;
